'use client';

import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';

import { Button } from '@kit/ui/button';
import { Label } from '@kit/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@kit/ui/sheet';
import { Switch } from '@kit/ui/switch';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import { openIosAppSettings } from '~/(tenant)/_lib/capacitor/app-tracking-transparency';

import { useNativePresentation } from '../native/_lib/use-native-presentation';
import { useTenantAnalyticsConsent } from '../tenant-analytics-consent-provider';

/**
 * The withdrawal path required by GDPR Art. 7(3): withdrawing consent must be
 * as easy as giving it. The banner is a one-shot prompt, so without this a
 * visitor who accepted could never change their mind.
 *
 * Opened from anywhere via the `tenant-analytics-consent:open` window event
 * (see `openTenantAnalyticsConsentPreferences`) rather than a trigger prop,
 * because the entry points live in ~12 per-tenant footers that this component
 * has no reference to.
 */
const OPEN_PREFERENCES_EVENT = 'tenant-analytics-consent:open';
const LINK_COUNT_CHANGE_EVENT = 'tenant-analytics-consent:link-count';

export function openTenantAnalyticsConsentPreferences() {
  window.dispatchEvent(new CustomEvent(OPEN_PREFERENCES_EVENT));
}

/**
 * Tracks how many footer links are currently mounted, so the floating launcher
 * can stand down when a surface already offers its own entry point. A counter
 * rather than a boolean because two footers can briefly overlap during a route
 * transition, and the launcher must not flash back in between them.
 */
let mountedCookieSettingsLinks = 0;

function notifyLinkCountChange() {
  window.dispatchEvent(new CustomEvent(LINK_COUNT_CHANGE_EVENT));
}

/**
 * Returns a registration function for a footer link to call from an effect.
 * The returned value is the effect's own cleanup, so mounting and unmounting
 * stay symmetrical.
 */
export function useRegisterCookieSettingsLink() {
  return useCallback(() => {
    mountedCookieSettingsLinks += 1;
    notifyLinkCountChange();

    return () => {
      mountedCookieSettingsLinks -= 1;
      notifyLinkCountChange();
    };
  }, []);
}

function subscribeToLinkCount(onStoreChange: () => void) {
  window.addEventListener(LINK_COUNT_CHANGE_EVENT, onStoreChange);

  return () =>
    window.removeEventListener(LINK_COUNT_CHANGE_EVENT, onStoreChange);
}

function getLinkCountSnapshot() {
  return mountedCookieSettingsLinks;
}

function getLinkCountServerSnapshot() {
  return 0;
}

interface IosTrackingState {
  allowed: boolean;
  decided: boolean;
}

export function TenantAnalyticsConsentPreferences({
  iosTracking,
  offersMarketing,
}: {
  /** Set in the iOS app when App Tracking Transparency owns the choice. */
  iosTracking: IosTrackingState | null;
  offersMarketing: boolean;
}) {
  if (iosTracking) {
    return <IosTrackingPreferences {...iosTracking} />;
  }

  return <CookiePreferences offersMarketing={offersMarketing} />;
}

function CookiePreferences({ offersMarketing }: { offersMarketing: boolean }) {
  const { consent, hasDecision, updateConsent } = useTenantAnalyticsConsent();
  const presentation = useNativePresentation();
  const [state, setState] = useState({
    open: false,
    analytics: consent.analytics,
    advertising: consent.advertising,
  });

  useEffect(() => {
    const onOpen = () => {
      // Re-seed from the stored decision each time, so the toggles always show
      // what is actually in effect rather than a stale draft.
      setState({
        open: true,
        analytics: consent.analytics,
        advertising: consent.advertising,
      });
    };

    window.addEventListener(OPEN_PREFERENCES_EVENT, onOpen);

    return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, onOpen);
  }, [consent.advertising, consent.analytics]);

  const close = () => setState((current) => ({ ...current, open: false }));

  const save = () => {
    updateConsent({
      analytics: state.analytics,
      advertising: offersMarketing ? state.advertising : false,
    });

    close();
  };

  const rejectAll = () => {
    updateConsent({ analytics: false, advertising: false });
    close();
  };

  return (
    <>
      <ConsentPreferencesLauncher hidden={!hasDecision} />

      <Sheet
        open={state.open}
        onOpenChange={(open) => setState((current) => ({ ...current, open }))}
      >
        <SheetContent
          {...presentation}
          className={cn(
            'flex w-full flex-col sm:max-w-md',
            presentation.className,
          )}
          data-test="tenant-analytics-consent-preferences"
        >
          <SheetHeader>
            <SheetTitle>
              <Trans i18nKey="common:tenantAnalyticsConsent.preferences.title" />
            </SheetTitle>

            <SheetDescription>
              <Trans i18nKey="common:tenantAnalyticsConsent.preferences.description" />
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 space-y-6 overflow-y-auto py-6">
            <ConsentCategory
              checked
              descriptionKey="common:tenantAnalyticsConsent.preferences.necessaryDescription"
              disabled
              id="consent-necessary"
              labelKey="common:tenantAnalyticsConsent.preferences.necessary"
              statusKey="common:tenantAnalyticsConsent.preferences.alwaysActive"
            />

            <ConsentCategory
              checked={state.analytics}
              descriptionKey="common:tenantAnalyticsConsent.preferences.analyticsDescription"
              id="consent-analytics"
              labelKey="common:tenantAnalyticsConsent.preferences.analytics"
              onCheckedChange={(analytics) =>
                setState((current) => ({ ...current, analytics }))
              }
            />

            {offersMarketing ? (
              <ConsentCategory
                checked={state.advertising}
                descriptionKey="common:tenantAnalyticsConsent.preferences.advertisingDescription"
                id="consent-advertising"
                labelKey="common:tenantAnalyticsConsent.preferences.advertising"
                onCheckedChange={(advertising) =>
                  setState((current) => ({ ...current, advertising }))
                }
              />
            ) : null}

            <p className="text-muted-foreground text-xs">
              <Trans i18nKey="common:tenantAnalyticsConsent.preferences.scopeNote" />
            </p>
          </div>

          <SheetFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
            {hasDecision ? (
              <Button
                data-test="tenant-analytics-consent-preferences-reject"
                onClick={rejectAll}
                type="button"
                variant="outline"
              >
                <Trans i18nKey="common:tenantAnalyticsConsent.preferences.rejectAll" />
              </Button>
            ) : null}

            <Button
              data-test="tenant-analytics-consent-preferences-save"
              onClick={save}
              type="button"
            >
              <Trans i18nKey="common:tenantAnalyticsConsent.preferences.save" />
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}

/**
 * Withdrawal path in the iOS app. Apple's "Allow Tracking" toggle in iOS
 * Settings is the single source of truth there — an in-page toggle could
 * neither grant tracking Apple denied nor stay in sync with Settings — so the
 * sheet shows the current answer and links straight to it.
 */
function IosTrackingPreferences({ allowed, decided }: IosTrackingState) {
  const presentation = useNativePresentation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onOpen = () => setOpen(true);

    window.addEventListener(OPEN_PREFERENCES_EVENT, onOpen);

    return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, onOpen);
  }, []);

  return (
    <>
      <ConsentPreferencesLauncher hidden={!decided} />

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          {...presentation}
          className={cn(
            'flex w-full flex-col sm:max-w-md',
            presentation.className,
          )}
          data-test="tenant-ios-tracking-preferences"
        >
          <SheetHeader>
            <SheetTitle>
              <Trans i18nKey="common:tenantAnalyticsConsent.iosTracking.title" />
            </SheetTitle>

            <SheetDescription>
              <Trans i18nKey="common:tenantAnalyticsConsent.iosTracking.description" />
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 py-6">
            <div className="flex items-start justify-between gap-4">
              <p className="font-medium">
                <Trans i18nKey="common:tenantAnalyticsConsent.preferences.advertising" />
              </p>

              <span
                className="text-muted-foreground shrink-0 text-sm font-medium"
                data-test="tenant-ios-tracking-status"
              >
                <Trans
                  i18nKey={
                    allowed
                      ? 'common:tenantAnalyticsConsent.iosTracking.allowed'
                      : 'common:tenantAnalyticsConsent.iosTracking.notAllowed'
                  }
                />
              </span>
            </div>
          </div>

          <SheetFooter>
            <Button
              data-test="tenant-ios-tracking-open-settings"
              onClick={openIosAppSettings}
              type="button"
            >
              <Trans i18nKey="common:tenantAnalyticsConsent.iosTracking.openSettings" />
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}

/**
 * Always-available re-entry point, rendered next to the sheet by the analytics
 * provider. The ~13 per-tenant landing footers each have a bespoke layout, so
 * injecting a link into every one of them would fight their designs and drift
 * as new tenants are added. A single unobtrusive anchor pinned to the page
 * corner reaches every surface — shared footer and custom footers alike — from
 * one place. Hidden while the banner is up (the banner already offers the
 * choice) and hidden entirely from print.
 */
function ConsentPreferencesLauncher({ hidden }: { hidden: boolean }) {
  const mountedLinks = useSyncExternalStore(
    subscribeToLinkCount,
    getLinkCountSnapshot,
    getLinkCountServerSnapshot,
  );

  if (hidden || mountedLinks > 0) {
    return null;
  }

  return (
    <button
      className="text-muted-foreground hover:text-foreground bg-background/80 fixed bottom-2 z-40 rounded-md px-2 py-1 text-xs backdrop-blur-sm transition-colors ltr:left-2 rtl:right-2 print:hidden"
      data-test="tenant-analytics-consent-launcher"
      onClick={openTenantAnalyticsConsentPreferences}
      type="button"
    >
      <Trans i18nKey="marketing:cookieSettings" />
    </button>
  );
}

function ConsentCategory({
  checked,
  descriptionKey,
  disabled,
  id,
  labelKey,
  onCheckedChange,
  statusKey,
}: {
  checked: boolean;
  descriptionKey: string;
  disabled?: boolean;
  id: string;
  labelKey: string;
  onCheckedChange?: (checked: boolean) => void;
  statusKey?: string;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="space-y-1">
        <Label className="font-medium" htmlFor={id}>
          <Trans i18nKey={labelKey} />
        </Label>

        <p className="text-muted-foreground text-sm">
          <Trans i18nKey={descriptionKey} />
        </p>
      </div>

      {statusKey ? (
        <span className="text-muted-foreground shrink-0 text-xs font-medium">
          <Trans i18nKey={statusKey} />
        </span>
      ) : (
        <Switch
          checked={checked}
          disabled={disabled}
          id={id}
          onCheckedChange={onCheckedChange}
        />
      )}
    </div>
  );
}
